import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

//MUI Icons
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

//Custom
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import modelObjekti from "@/models/objekti";
import ObjectPicker from "../ObjectAnalysisToolbar/ObjectPicker";
import ToggleTableButton from "../ObjectAnalysisToolbar/ToggleTableButton";
import ObjectAnalysisTable from "../ObjectAnalysisToolbar/ObjectAnalysisTable";
import objektiService from "@/services/objektiService";
import LoaderContext from "@/components/LoaderContext/LoaderContext";

//Types
import { DCRecord } from "@/@types/lib/dataController";
import { PickerItem } from "@/@types/controls/controls";
import QualityControlPicker from "./QualityControlPicker";
import { Tag } from "@mui/icons-material";

export type ObjectAnalysisInfoStripProps = {
  records: Array<DCRecord>;
  selectedRecord: DCRecord | null;
  onRecordSelect: (id: number, record: DCRecord | null) => void;
  onRecordChange: () => void;

  qcRecords: DCRecord[];
  selectedQcRecord: DCRecord | null;
  onQcRecordSelect: (id: number, record: DCRecord | null) => void;

  isQcChecked: boolean;
  qcObjectFilter: DCRecord[];
  selectedQcFunction: DCRecord | null;
};

const QualityControlToolbar = (props: ObjectAnalysisInfoStripProps) => {
  const snackbarContext = useContext(SnackbarContext);
  const loaderContext = useContext(LoaderContext);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tableOpen, setTableOpen] = useState(false);

  const { t } = useTranslation();

  const {
    records,
    selectedRecord,
    onRecordSelect,
    onRecordChange,
    qcRecords,
    selectedQcRecord,
    onQcRecordSelect,
    isQcChecked,
    qcObjectFilter,
    selectedQcFunction,
  } = props;

  const qcFunctionSelected = !!selectedQcFunction;

  const filteredRecords = useMemo(() => {
    if (!records.length) return []; // page didnt load yet
    if (!selectedQcFunction) return records; // no qc function selected
    if (!qcObjectFilter.length) return []; // api didnt return anything
    return qcObjectFilter // show only recs that are contained in qc filter, add label
      .map((qcRec) => {
        const objektRecord = records.find((rec) => rec.id === qcRec.id);
        return {
          ...qcRec,
          ...objektRecord,
        };
      });
  }, [records, qcObjectFilter, selectedQcFunction]);

  const items = records.map((x) => {
    return { label: x.id?.toString(), value: x.id } as PickerItem;
  });
  const selectedItem = selectedRecord
    ? ({
        label: selectedRecord.id?.toString(), // why string?
        value: selectedRecord.id,
      } as PickerItem)
    : null;

  const qcItems = qcRecords.map((qcRec) => {
    return {
      value: qcRec.id as number,
      label: qcRec.title as string,
    } as PickerItem;
  });

  const selectedQcItem = selectedQcRecord
    ? ({
        value: selectedQcRecord.id as number,
        label: selectedQcRecord.title as string,
      } as PickerItem)
    : null;

  // useEffect(() => {
  //   if (selectedRecord) {
  //     if (events) {
  //       unregisterEvents();
  //     }
  //     registerEvents();
  //     setEvents(true);
  //     return () => unregisterEvents();
  //   }
  //   return () => {};
  // }, [selectedRecord, isQcChecked]);
  //
  // const eventRegistrationFn = (evt: KeyboardEvent) => {
  //   if (evt.altKey && evt.shiftKey) {
  //     switch (evt.code) {
  //       case "KeyE":
  //         handleToggleDone();
  //         break;
  //     }
  //   }
  // };
  //
  // const registerEvents = () => {
  //   window.addEventListener("keydown", eventRegistrationFn);
  // };
  //
  // const unregisterEvents = () => {
  //   window.removeEventListener("keydown", eventRegistrationFn);
  // };

  const hanleRecordSelect = (id: number | null) => {
    if (id) {
      const rec = records.find((x) => x.id === id);
      if (rec) {
        onRecordSelect(id, rec);
      } else {
        onRecordSelect(-1, null);
      }
    } else {
      onRecordSelect(-1, null);
    }
  };

  const hanleQcRecordSelect = (id: number | null) => {
    if (id) {
      const rec = qcRecords.find((x) => x.id === id);
      if (rec) {
        onQcRecordSelect(id, rec);
      } else {
        onQcRecordSelect(-1, null);
      }
    } else {
      onQcRecordSelect(-1, null);
    }
  };

  const handleToggleTableOpen = (
    evt: React.MouseEvent<HTMLButtonElement>,
    newValue: boolean
  ) => {
    setTableOpen(newValue);

    if (newValue === true) {
      setAnchorEl(evt.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  const handleCloseTable = () => {
    setTableOpen(false);
    setAnchorEl(null);
  };

  const handleToggleQcChecked = () => {
    loaderContext.toggleLoading(true);
    const newValue = !isQcChecked;

    if (selectedRecord && selectedRecord.id) {
      objektiService
        .changeQcState(selectedRecord.id as number, newValue)
        .then((resp) => {
          if (resp.success) {
            onRecordChange();
            snackbarContext.showNotification("update", "success");
          }
        })
        .catch((err) => {
          snackbarContext.showNotification(
            `${t("objekti.fail_set_qc")}\n${err.error.message}`.replaceAll(
              ":",
              " - "
            ),
            "error"
          );
        })
        .finally(() => {
          loaderContext.toggleLoading(false);
        });
    }
  };

  const hasRecords = records.length > 0 ? true : false;
  const hasSelectedRecord = selectedRecord ? true : false;

  return (
    <Toolbar
      variant="dense"
      sx={{
        borderSize: 2,
        borderStyle: "solid",
        borderColor: "primary.light",
        gap: 1,
        px: "8px !important",
      }}
    >
      <Typography>{t("objekti.picker_label")}</Typography>{" "}
      {/* TODO: add tokens */}
      <ObjectPicker
        items={items}
        selectedItem={selectedItem}
        onChange={hanleRecordSelect}
      />
      <Typography>{t("qc.picker_label")}</Typography>
      <QualityControlPicker
        items={qcItems}
        selectedItem={selectedQcItem}
        onChange={hanleQcRecordSelect}
      />
      {qcObjectFilter.length ? (
        <Tooltip title={t("qc.num_qc_objects") as string}>
          <Chip
            label={qcObjectFilter.length}
            icon={<Tag />}
            sx={{ mx: 1 }}
          />
        </Tooltip>
      ) : null}
      {selectedRecord ? (
        <>
          {/*<Chip
            color="primary"
            variant={isGlavniObjekt ? "filled" : "outlined"}
            label={t("buttons.gl_obj")}
            icon={isGlavniObjekt ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            sx={{ mx: 1 }}
            clickable={!isPregledan}
            onClick={isPregledan ? () => {} : handleToggleGlavniObjekt}
          />
          <Tooltip title={t("objekti.adresa_objekta") as string}>
            <Chip
              label={selectedRecord.adresa_full}
              icon={<AdresaIcon />}
              sx={{ mx: 1 }}
            />
          </Tooltip>*/}
        </>
      ) : null}
      <ToolbarFillContent />
      <Tooltip
        title={
          isQcChecked || !hasSelectedRecord
            ? ""
            : (t("qc.tooltips.check") as string)
        }
      >
        <Chip
          color="primary"
          variant={isQcChecked ? "filled" : "outlined"}
          label={t("qc.buttons.check")}
          icon={isQcChecked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
          sx={{ mx: 1 }}
          clickable={true}
          onClick={handleToggleQcChecked}
          disabled={!hasSelectedRecord}
        />
      </Tooltip>
      <ToggleTableButton
        open={tableOpen}
        onClick={handleToggleTableOpen}
        disabled={!hasRecords}
      />
      <Popover
        id="table_popover"
        open={true}
        anchorEl={anchorEl}
        onClose={handleCloseTable}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        style={{ visibility: tableOpen ? "visible" : "hidden" }}
      >
        <ObjectAnalysisTable
          records={filteredRecords}
          onRowSelect={hanleRecordSelect}
          model={modelObjekti}
          titleToken={
            qcFunctionSelected
              ? "qc.titles.objekti_filtered"
              : "qc.titles.objekti_all"
          }
          viewName={
            qcFunctionSelected ? "quality_control_filtered" : "quality_control"
          }
        />
      </Popover>
    </Toolbar>
  );
};

export default QualityControlToolbar;
